import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import './App.css'
import striptags from 'striptags'
import {
  HashRouter,
  useNavigate,
  useParams,
  Outlet,
  Routes,
  Route,
  Link,
  useLocation,
  useOutletContext,
} from 'react-router-dom'

// eslint-disable-next-line import/no-webpack-loader-syntax
const Worker = require('workerize-loader!./search.worker')

interface SearchResult {
  html: string
  season: number
  episode: number
  id: number
  type?: string
}

interface TextStyle {
  fontSize: number
  color: string
  fontFamily: string
}

interface Stats {
  documentCount: number
  dialogCount: number
  intervalCount: number
  sentenceCount: number
}

function VisitsDisplay () {
  const [count, setCount] = useState<number | null>(null)

  useEffect(() => {
    fetch('/api/visits')
      .then(r => r.json())
      .then(d => setCount(d.count))
      .catch(() => {})
    if (!sessionStorage.getItem('pv_visited')) {
      sessionStorage.setItem('pv_visited', '1')
      fetch('/api/visits', { method: 'POST' })
        .then(r => r.json())
        .then(d => setCount(d.count))
        .catch(() => {})
    }
  }, [])

  return (
    <div className="refresh-btn visits-display" title="Total visits">
      ✦ {count !== null ? count.toLocaleString() : '…'} visits
    </div>
  )
}

const LIKE_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315]

function LikeButton () {
  const [count, setCount] = useState<number | null>(null)
  const [hasLiked, setHasLiked] = useState(false)
  const [burst, setBurst] = useState(false)

  useEffect(() => {
    fetch('/api/likes')
      .then(r => r.json())
      .then(d => setCount(d.count))
      .catch(() => {})
    setHasLiked(!!localStorage.getItem('pv_liked'))
  }, [])

  const handleLike = async () => {
    if (hasLiked || burst) return
    setBurst(true)
    try {
      const r = await fetch('/api/likes', { method: 'POST' })
      const d = await r.json()
      setCount(d.count)
    } catch {}
    setHasLiked(true)
    localStorage.setItem('pv_liked', '1')
    setTimeout(() => setBurst(false), 700)
  }

  return (
    <div className="like-wrap">
      {burst && LIKE_ANGLES.map((angle, i) => {
        const rad = angle * Math.PI / 180
        const tx = Math.round(Math.cos(rad) * 30)
        const ty = Math.round(Math.sin(rad) * 30)
        return (
          <span
            key={i}
            className="like-particle"
            style={{ '--tx': `${tx}px`, '--ty': `${ty}px` } as React.CSSProperties}
          />
        )
      })}
      <button
        className={`refresh-btn like-btn${hasLiked ? ' liked' : ''}${burst ? ' burst' : ''}`}
        onClick={handleLike}
        title={hasLiked ? 'Already liked!' : 'Like this site'}
      >
        ♥{count !== null ? ` ${count.toLocaleString()}` : ' …'} likes
      </button>
    </div>
  )
}

function Main ({
  didSearch,
  searchResults,
  searchCriteria,
  ready,
  workerInstance,
}: {
  didSearch: boolean,
  searchResults: SearchResult[],
  searchCriteria: string,
  ready: boolean,
  workerInstance: any,
}) {
  const [randomFrames, setRandomFrames] = useState<SearchResult[]>([])
  const [stats, setStats] = useState<Stats | null>(null)

  const frameCount = () => window.innerWidth <= 600 ? 12 : 25

  useEffect(() => {
    if (!ready || !workerInstance) return
    Promise.all(Array.from({ length: frameCount() }, () => workerInstance.loadRandomFrame()))
      .then((frames: any[]) => setRandomFrames(frames.filter(Boolean)))
    workerInstance.getStats().then((s: any) => {
      if (s) setStats(s)
    })
  }, [ready]) // eslint-disable-line

  const isSearching = searchCriteria.trim().length > 0
  const displayResults = isSearching ? searchResults : randomFrames

  return <>
    {displayResults.length === 0 && (
      <div className="status-text">
        {!ready ? 'Initializing…' : (isSearching && didSearch ? 'No results found' : (isSearching ? 'Keep typing to search' : 'Loading specimens…'))}
      </div>
    )}
    {displayResults.length > 0 && <div>
      {!isSearching && (
        <>
        <div className="scenes-header">
          <div className="scenes-left">
            <LikeButton />
            <VisitsDisplay />
          </div>
          <div className="scenes-center">
            <p className="dedication">✨ Made with ❤️ for a very special Queen ✨</p>
            <p className="stats-bar">
              12 Episodes
              {stats && <> &nbsp;&middot;&nbsp; {stats.dialogCount.toLocaleString()} dialog frames</>}
              {stats && <> &nbsp;&middot;&nbsp; {stats.intervalCount.toLocaleString()} silent scenes</>}
              {stats && <> &nbsp;&middot;&nbsp; {stats.documentCount.toLocaleString()} total frames</>}
            </p>
          </div>
          <button className="refresh-btn" onClick={() => {
            Promise.all(Array.from({ length: frameCount() }, () => workerInstance.loadRandomFrame()))
              .then((frames: any[]) => setRandomFrames(frames.filter(Boolean)))
          }}>Refresh</button>
        </div>
        </>
      )}
      <ul aria-description="Search results">
        {displayResults.map((doc: SearchResult) => (
          <li key={`${doc.season}-${doc.episode}-${doc.id}`} className={`searchResult${doc.type === 'interval' ? ' silent' : ''}`}>
            <Link to={framePath(doc)} className="button">
              <img
                src={`${process.env.REACT_APP_ASSETS_URL}/${doc.season}x${('' + doc.episode).padStart(2, '0')}/${doc.id}_thumbnail.${process.env.REACT_APP_ASSETS_EXTENSION || 'png'}`}
                alt=""
                className="thumbnail"
              />
              {doc.type !== 'interval' && doc.html && (
                <span>{striptags(doc.html)}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>}
  </>
}

function Mosaic ({
  frames,
  setFrames,
  topActions,
}: {
  frames: string[],
  setFrames: (_: string[]) => void,
  topActions?: React.ReactNode,
}) {
  const [cols, setCols] = useState(2)

  const remove = (i: number) => setFrames(frames.filter((_, idx) => idx !== i))

  const swap = (a: number, b: number) => {
    if (b < 0 || b >= frames.length) return
    const next = [...frames];
    [next[a], next[b]] = [next[b], next[a]]
    setFrames(next)
  }

  const download = () => {
    const fw = 360
    const fh = Math.round(fw * 9 / 16)
    const dc = Math.min(cols, frames.length)
    const rows = Math.ceil(frames.length / dc)
    const canvas = document.createElement('canvas')
    canvas.width = dc * fw
    canvas.height = rows * fh
    const ctx = canvas.getContext('2d')!
    let done = 0
    frames.forEach((src, i) => {
      const img = new Image()
      img.onload = () => {
        ctx.drawImage(img, (i % dc) * fw, Math.floor(i / dc) * fh, fw, fh)
        if (++done === frames.length) {
          const a = document.createElement('a')
          a.download = 'planetvesta-mosaic.png'
          a.href = canvas.toDataURL()
          a.click()
        }
      }
      img.src = src
    })
  }

  const partialFill = frames.length > 0 ? (cols - (frames.length % cols)) % cols : 0
  const slots: (string | null)[] = frames.length > 0
    ? [...frames, ...Array(partialFill).fill(null)]
    : []

  return (
    <div className="mosaic-container">
      <div className="mosaic-top-bar">
        <div className="mosaic-top-actions">{topActions}</div>
        <div className="mosaic-col-control">
          <span>Columns</span>
          <button onClick={() => setCols(c => Math.max(1, c - 1))} disabled={cols <= 1}>−</button>
          <span className="col-count">{cols}</span>
          <button onClick={() => setCols(c => Math.min(4, c + 1))} disabled={cols >= 4}>+</button>
        </div>
      </div>
      {frames.length > 0 && (
        <>
          <div
            className="mosaic-grid"
            style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
          >
            {slots.map((src, i) => {
              if (src !== null) {
                return (
                  <div key={i} className="mosaic-panel">
                    <img src={src} alt="" />
                    {i - cols >= 0 && (
                      <button className="mosaic-move mosaic-move-up" onClick={() => swap(i, i - cols)} title="Move up">↑</button>
                    )}
                    {i + cols < frames.length && (
                      <button className="mosaic-move mosaic-move-down" onClick={() => swap(i, i + cols)} title="Move down">↓</button>
                    )}
                    {i > 0 && (
                      <button className="mosaic-move mosaic-move-left" onClick={() => swap(i, i - 1)} title="Move left">←</button>
                    )}
                    {i < frames.length - 1 && (
                      <button className="mosaic-move mosaic-move-right" onClick={() => swap(i, i + 1)} title="Move right">→</button>
                    )}
                    <button className="mosaic-remove" onClick={() => remove(i)} title="Remove">×</button>
                  </div>
                )
              }
              return <div key={`e-${i}`} className="mosaic-empty" />
            })}
          </div>
          <div className="mosaic-actions">
            <button onClick={download}>Download</button>
            <button onClick={() => setFrames([])}>Clear</button>
          </div>
        </>
      )}
    </div>
  )
}

function Frame ({
  workerInstance,
  ready,
  searchCriteria,
  mosaicFrames,
  setMosaicFrames,
}: {
  workerInstance: any,
  ready: boolean,
  searchCriteria: string,
  mosaicFrames: string[],
  setMosaicFrames: (_: string[]) => void,
}) {
  const { season, episode, id } = useParams()
  const [currFrame, setCurrFrame] = useState<SearchResult | null>(null)
  const [prevFrame, setPrevFrame] = useState<SearchResult | null>(null)
  const [nextFrame, setNextFrame] = useState<SearchResult | null>(null)
  const [caption, setCaption] = useState('')
  const [textStyle, setTextStyle] = useState<TextStyle>({
    fontSize: 48,
    color: '#348b77',
    fontFamily: 'planetvesta-font',
  })
  const [customImageUrl, setCustomImageUrl] = useState<string | null>(null)
  const [presetColors, setPresetColors] = useState<string[]>(['#ffffff', '#348b77', '#6ea682', '#174745', '#ffda1a'])

  useKeyboardNavigation()

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const getCurrentFrame = useCallback((): Promise<string | undefined> => {
    const canvas = canvasRef.current as (HTMLCanvasElement | null)
    if (!canvas || !currFrame) return Promise.resolve(undefined)
    const ctx = canvas.getContext('2d')
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
    canvas.width = 720
    canvas.height = 405
    const promise: Promise<string> = new Promise((resolve, reject) => {
      const image = new Image()
      if (!customImageUrl) image.crossOrigin = 'Anonymous'
      image.src = customImageUrl || imageUrl(currFrame)
      image.onload = function () {
        if (!ctx) return reject(new Error('no context'))
        const padding = 10
        canvas.width = image.width
        canvas.height = image.height
        const x = this as any
        ctx.drawImage(x, 0, 0, x.width, x.height)
        let size = textStyle.fontSize
        ctx.font = `${size}px ${textStyle.fontFamily}`
        ctx.fillStyle = textStyle.color
        ctx.strokeStyle = 'rgba(0,0,0,0.9)'
        ctx.textBaseline = 'top'
        ctx.textAlign = 'center'
        const lines = caption.split('\n')
        let height = size - 4
        lines.forEach((line) => {
          while (size > 10) {
            const measure = ctx.measureText(line)
            if (measure.width > image.width - 2 * padding) {
              size--
              ctx.font = `${size}px ${textStyle.fontFamily}`
            } else {
              break
            }
            if (measure.actualBoundingBoxDescent < size) {
              height = measure.actualBoundingBoxDescent
            }
          }
        })
        lines.reverse().forEach((line, i) => {
          const x = image.width / 2
          const y = image.height - height * (1 + i) - padding
          ctx.lineWidth = 6
          ctx.strokeText(line, x, y)
          ctx.fillText(line, x, y)
        })
        resolve(canvas.toDataURL())
      }
    })
    return promise
  }, [currFrame, caption, textStyle, customImageUrl])

  const addCurrentFrameToMosaic = async () => {
    const currentFrame = await getCurrentFrame()
    if (!currentFrame) return
    setMosaicFrames([...mosaicFrames, currentFrame])
  }

  useEffect(() => {
    (async () => {
      if (imageRef.current) {
        imageRef.current.src = (await getCurrentFrame()) || ''
      }
    })()
  }, [getCurrentFrame])

  useEffect(() => {
    if (!ready) return
    if (!currFrame ||
      currFrame.season !== parseInt(season || '', 10) ||
      currFrame.episode !== parseInt(episode || '', 10) ||
      currFrame.id !== parseInt(id || '', 10)) {
      setCustomImageUrl(null)
      workerInstance?.loadFrame(season, episode, id).then((data: SearchResult) => {
        if (!data) return
        setCurrFrame(data)
        // Silent frames start with empty caption; dialog frames use subtitle text
        setCaption(data.type === 'interval' ? '' : striptags(data.html))
      })
      workerInstance?.loadFrame(season, episode, id, -1).then(setPrevFrame)
      workerInstance?.loadFrame(season, episode, id, 1).then(setNextFrame)
    }
  }, [currFrame, ready, workerInstance, season, episode, id])

  if (!currFrame) return <>Loading…</>

  return <div id="selectedItem">
    <Link
      to={{ pathname: '/', search: searchCriteria && new URLSearchParams({ s: searchCriteria }).toString() }}
      className="button back"
    >← Back</Link>
    <canvas ref={canvasRef}></canvas>
    <img ref={imageRef} alt="" />
    <div id="frameNavigation">
      {prevFrame && <Link to={framePath(prevFrame)} className="button" rel="prev">← Previous</Link>}
      {nextFrame && <Link to={framePath(nextFrame)} className="button" rel="next">Next →</Link>}
    </div>
    <p className="frame-meta">
      Season {currFrame.season} &nbsp;/&nbsp; Episode {currFrame.episode} &nbsp;&mdash;&nbsp;
      {new Date(currFrame.id).toISOString().substring(11, 19)}
      {currFrame.type === 'interval' && <span className="silent-badge">Silent Scene</span>}
    </p>
    <textarea value={caption} onChange={(e) => setCaption(e.target.value)} aria-label="Caption" placeholder={currFrame.type === 'interval' ? 'Add a caption…' : ''} />
    <div className="text-style-controls">
      <label className="style-field">
        <span>Size</span>
        <input
          type="range" min={10} max={72} value={textStyle.fontSize}
          onChange={(e) => setTextStyle(s => ({ ...s, fontSize: parseInt(e.target.value) }))}
        />
        <span className="style-val">{textStyle.fontSize}px</span>
      </label>
      <div className="style-field">
        <span>Color</span>
        {presetColors.map((pc, i) => (
          <div key={i} className={`color-swatch-wrap${i >= 3 ? ' desktop-only' : ''}`}>
            <button
              className="color-swatch-btn"
              style={{ background: pc }}
              onClick={() => setTextStyle(s => ({ ...s, color: pc }))}
              title="Apply color"
            />
            <label className="color-swatch-edit" title="Edit preset color">
              ✏
              <input
                type="color"
                value={pc}
                onChange={(e) => {
                  const next = [...presetColors]
                  next[i] = e.target.value
                  setPresetColors(next)
                }}
              />
            </label>
          </div>
        ))}
      </div>
      <label className="style-field">
        <span>Font</span>
        <select
          value={textStyle.fontFamily}
          onChange={(e) => setTextStyle(s => ({ ...s, fontFamily: e.target.value }))}
        >
          <option value="planetvesta-font">Default</option>
          <option value="Impact">Impact</option>
          <option value="Arial, sans-serif">Sans</option>
          <option value="Georgia, serif">Georgia</option>
          <option value="'Courier New', monospace">Courier</option>
          <option value="Bangers, cursive">Bangers</option>
          <option value="Pacifico, cursive">Pacifico</option>
          <option value="OpenDyslexic, sans-serif">Dyslexic</option>
        </select>
      </label>
    </div>
    <Mosaic
      frames={mosaicFrames}
      setFrames={setMosaicFrames}
      topActions={<>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) { setCustomImageUrl(URL.createObjectURL(file)); setCaption('') }
          }}
        />
        <button onClick={addCurrentFrameToMosaic}>Add to mosaic</button>
        <button onClick={() => fileInputRef.current?.click()}>
          {customImageUrl ? '↺ Swap image' : 'Use custom image'}
        </button>
        {customImageUrl && (
          <button onClick={() => { setCustomImageUrl(null); setCaption(currFrame.type === 'interval' ? '' : striptags(currFrame.html)) }}>Reset to original</button>
        )}
      </>}
    />
    <GifMaker currFrame={currFrame} workerInstance={workerInstance} />
    {prevFrame && <link rel="preload" href={imageUrl(prevFrame)} as="image" crossOrigin="" />}
    {nextFrame && <link rel="preload" href={imageUrl(nextFrame)} as="image" crossOrigin="" />}
  </div>
}

type GifFrame = { frame: SearchResult, thumbUrl: string, fullUrl: string, caption?: string }

function GifEditPreview ({ url, caption }: { url: string | undefined, caption: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !url) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const img = new Image()
    img.crossOrigin = 'Anonymous'
    img.onload = () => {
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)
      if (caption.trim()) drawGifCaption(ctx, caption, img.width, img.height)
    }
    img.src = url
  }, [url, caption])

  return <canvas ref={canvasRef} className="gif-edit-img" />
}

function drawGifCaption (ctx: CanvasRenderingContext2D, caption: string, w: number, h: number) {
  const padding = 8
  const fontFace = 'planetvesta-font, Impact, sans-serif'
  let size = 22
  const setFont = (s: number) => { ctx.font = `${s}px ${fontFace}` }
  ctx.fillStyle = '#ffda1a'
  ctx.strokeStyle = 'rgba(0,0,0,0.9)'
  ctx.textBaseline = 'top'
  ctx.textAlign = 'center'
  const lines = caption.split('\n').filter(l => l.trim())
  if (!lines.length) return
  setFont(size)
  lines.forEach(line => {
    while (size > 8 && ctx.measureText(line).width > w - 2 * padding) {
      size--
      setFont(size)
    }
  })
  const lineH = size + 3
  lines.reverse().forEach((line, i) => {
    const x = w / 2
    const y = h - lineH * (i + 1) - padding
    ctx.lineWidth = 4
    setFont(size)
    ctx.strokeText(line, x, y)
    ctx.fillText(line, x, y)
  })
}

function GifMaker ({
  currFrame,
  workerInstance,
}: {
  currFrame: SearchResult,
  workerInstance: any,
}) {
  const mobile = () => window.innerWidth <= 600
  const batchSize = () => mobile() ? 3 : 6
  const initialCount = () => mobile() ? 12 : 24

  const [open, setOpen] = useState(false)
  const [delay, setDelay] = useState(1000)
  const [showFrames, setShowFrames] = useState<GifFrame[]>([])
  const [customUrls, setCustomUrls] = useState<string[]>([])
  const [generating, setGenerating] = useState(false)
  const [progress, setProgress] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)
  const [showText, setShowText] = useState(true)
  const [editIdx, setEditIdx] = useState<number | null>(null)
  const [editCaption, setEditCaption] = useState('')
  const nextDelta = useRef(0)
  const fileRef = useRef<HTMLInputElement>(null)

  const loadBatched = useCallback(async (startDelta: number, count: number, replace: boolean) => {
    if (!workerInstance) return
    setLoadingMore(true)
    const size = batchSize()
    for (let i = 0; i < count; i += size) {
      const n = Math.min(size, count - i)
      const results = await Promise.all(
        Array.from({ length: n }, (_, j) =>
          workerInstance.loadFrame(
            String(currFrame.season), String(currFrame.episode), String(currFrame.id),
            startDelta + i + j
          )
        )
      )
      const valid = (results.filter(Boolean) as SearchResult[]).filter((f: SearchResult) => f.id)
      const mapped: GifFrame[] = valid.map((f: SearchResult) => ({
        frame: f,
        thumbUrl: thumbnailUrl(f),
        fullUrl: imageUrl(f),
      }))
      if (replace && i === 0) {
        setShowFrames(mapped)
      } else {
        setShowFrames(fs => [...fs, ...mapped])
      }
    }
    setLoadingMore(false)
  }, [currFrame, workerInstance]) // eslint-disable-line

  useEffect(() => {
    if (open) {
      nextDelta.current = 0
      const total = initialCount()
      loadBatched(0, total, true).then(() => { nextDelta.current = total })
    }
  }, [open]) // eslint-disable-line

  const MAX_SHOW_FRAMES = 100
  const addScenes = async (count: number) => {
    if (!workerInstance || loadingMore) return
    const remaining = MAX_SHOW_FRAMES - showFrames.length
    if (remaining <= 0) return
    const actual = Math.min(count, remaining)
    const start = nextDelta.current
    nextDelta.current += actual
    await loadBatched(start, actual, false)
  }

  const generate = async () => {
    type Src = { url: string, caption?: string }
    const allSrcs: Src[] = [
      ...showFrames.map(f => ({ url: f.fullUrl, caption: showText ? (f.caption !== undefined ? f.caption : striptags(f.frame.html)) : undefined })),
      ...customUrls.map(url => ({ url })),
    ]
    if (!allSrcs.length) return
    setGenerating(true)
    setProgress(0)
    const GIF = require('gif.js') // eslint-disable-line
    const gif = new GIF({
      workers: 4,
      quality: 10,
      width: 480,
      height: 270,
      workerScript: `${process.env.PUBLIC_URL}/gif.worker.js`,
    })
    for (const { url, caption } of allSrcs) {
      const canvas = document.createElement('canvas')
      canvas.width = 480
      canvas.height = 270
      const ctx = canvas.getContext('2d')!
      await new Promise<void>((resolve) => {
        const img = new Image()
        img.crossOrigin = 'Anonymous'
        img.onload = () => {
          ctx.drawImage(img, 0, 0, 480, 270)
          if (caption) drawGifCaption(ctx, caption, 480, 270)
          resolve()
        }
        img.onerror = () => resolve()
        img.src = url
      })
      gif.addFrame(canvas, { delay, copy: true })
    }
    gif.on('progress', (p: number) => setProgress(Math.round(p * 100)))
    gif.on('finished', (blob: Blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.download = 'planetvesta.gif'
      a.href = url
      a.click()
      URL.revokeObjectURL(url)
      setGenerating(false)
    })
    gif.render()
  }

  const removeFrame = (i: number) => setShowFrames(fs => fs.filter((_, idx) => idx !== i))
  const removeCustom = (i: number) => setCustomUrls(us => us.filter((_, idx) => idx !== i))

  const openEdit = (i: number) => {
    const f = showFrames[i]
    setEditCaption(f.caption !== undefined ? f.caption : striptags(f.frame.html))
    setEditIdx(i)
  }
  const saveEdit = () => {
    if (editIdx === null) return
    setShowFrames(fs => fs.map((f, i) => i === editIdx ? { ...f, caption: editCaption } : f))
    setEditIdx(null)
  }

  const totalFrames = showFrames.length + customUrls.length
  const duration = ((totalFrames * delay) / 1000).toFixed(1)
  const addN = batchSize()

  return (
    <div className="gif-maker">
      {!open
        ? <button className="gif-maker-toggle" onClick={() => setOpen(true)}>Make GIF</button>
        : (<>
        <div className="gif-maker-header">
          <span className="gif-maker-title">GIF Maker</span>
          <span className="gif-maker-duration">~{duration}s · {totalFrames} frames</span>
          <span className="gif-maker-hint">Double-click any scene to edit</span>
          <button className="gif-maker-close" onClick={() => setOpen(false)}>✕</button>
        </div>
        <div className="gif-maker-controls">
          <label className="gif-control">
            <span>Speed</span>
            <input type="range" min={50} max={10000} step={50} value={delay}
              onChange={e => setDelay(parseInt(e.target.value))}
              disabled={generating}
            />
            <span className="gif-val">{delay}ms/frame</span>
          </label>
          <div className="gif-control gif-control-toggle">
            <span>Subtitles</span>
            <button
              className={`gif-text-toggle${showText ? ' active' : ''}`}
              onClick={() => setShowText(v => !v)}
              disabled={generating}
            >{showText ? 'On' : 'Off'}</button>
          </div>
        </div>
        {loadingMore && showFrames.length === 0 && <p className="gif-status">Loading frames…</p>}
        {(showFrames.length > 0 || customUrls.length > 0) && (
          <div className="gif-grid">
            {showFrames.map((f, i) => (
              <div key={f.frame.id} className="gif-thumb" onDoubleClick={() => openEdit(i)}>
                <img src={f.thumbUrl} alt="" />
                {f.caption !== undefined && f.caption !== striptags(f.frame.html) && (
                  <span className="gif-thumb-edited" title="Caption edited">✎</span>
                )}
                <button className="gif-thumb-remove" onClick={() => removeFrame(i)}>×</button>
              </div>
            ))}
            {customUrls.map((url, i) => (
              <div key={`c-${i}`} className="gif-thumb gif-thumb-custom">
                <img src={url} alt="" />
                <button className="gif-thumb-remove" onClick={() => removeCustom(i)}>×</button>
              </div>
            ))}
          </div>
        )}
        {generating && (
          <div className="gif-progress">
            <div className="gif-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        )}
        <div className="gif-actions">
          <button onClick={() => addScenes(1)} disabled={loadingMore || generating}>+ Scene</button>
          <button onClick={() => addScenes(addN)} disabled={loadingMore || generating}>+ {addN} Scenes</button>
          <button onClick={() => { setShowFrames([]); setCustomUrls([]) }} disabled={generating || totalFrames === 0}>Clear all</button>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
            onChange={e => {
              const urls = Array.from(e.target.files || []).map(f => URL.createObjectURL(f))
              setCustomUrls(prev => [...prev, ...urls])
            }}
          />
          <button onClick={() => fileRef.current?.click()} disabled={generating}>Add images</button>
          <button className="gif-generate-btn" onClick={generate} disabled={generating || totalFrames === 0}>
            {generating ? `Encoding… ${progress}%` : 'Generate GIF'}
          </button>
        </div>
        {loadingMore && showFrames.length > 0 && <p className="gif-status">Loading…</p>}
        {editIdx !== null && (() => {
          const ef = showFrames[editIdx]
          return (
            <div className="gif-edit-overlay" onClick={() => setEditIdx(null)}>
              <div className="gif-edit-modal" onClick={e => e.stopPropagation()}>
                <GifEditPreview url={ef?.fullUrl} caption={showText ? editCaption : ''} />
                <p className="gif-edit-meta">
                  Season {ef?.frame.season} &nbsp;/&nbsp;
                  Episode {ef?.frame.episode} &nbsp;&mdash;&nbsp;
                  {new Date(ef?.frame.id).toISOString().substring(11, 19)}
                </p>
                <textarea
                  className="gif-edit-textarea"
                  value={editCaption}
                  onChange={e => setEditCaption(e.target.value)}
                  placeholder="Caption text…"
                />
                <div className="gif-edit-actions">
                  <button onClick={() => setEditCaption('')}>Clear text</button>
                  <button onClick={() => setEditIdx(null)}>Cancel</button>
                  <button className="gif-edit-save" onClick={saveEdit}>Save</button>
                </div>
              </div>
            </div>
          )
        })()}
      </>)}
    </div>
  )
}

function useKeyboardNavigation () {
  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      if (document.activeElement?.matches('input, textarea, select')) return
      switch (event.key) {
        case 'ArrowLeft':
        case 'j':
          document.querySelector<HTMLAnchorElement>('a[rel=prev]')?.click()
          break
        case 'ArrowRight':
        case 'k':
          document.querySelector<HTMLAnchorElement>('a[rel=next]')?.click()
          break
      }
    }
    window.addEventListener('keydown', keyDownHandler)
    return () => { window.removeEventListener('keydown', keyDownHandler) }
  }, [])
}

function WorkerTrigger ({
  searchCriteria,
  setSearchCriteria,
  setDidSearch,
  setSearchResults,
  setReady,
  workerInstance,
  ready,
  searchResults,
  didSearch,
  mosaicFrames,
  setMosaicFrames,
}: {
  searchCriteria: string,
  setSearchCriteria: (_: string) => void,
  setDidSearch: (_: boolean) => void,
  setSearchResults: (_: SearchResult[]) => void,
  setReady: (_: boolean) => void,
  workerInstance: any,
  ready: boolean,
  searchResults: SearchResult[],
  didSearch: boolean,
  mosaicFrames: string[],
  setMosaicFrames: (_: string[]) => void,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const placeholders = (process.env.REACT_APP_PLACEHOLDER || '').split(',')
  const placeholderIndex = useMemo(() => Math.floor(Math.random() * placeholders.length), []) // eslint-disable-line

  const processMessage = ({ data }: any) => {
    const [t, params] = [data[0], data[1]]
    if (!t) return
    switch (t) {
      case 'setReady': setReady(params); break
      case 'setSearchResults': setSearchResults(params); break
      case 'setDidSearch': setDidSearch(params); break
      default: console.error('unexpected message type: ' + t); break
    }
  }

  useEffect(() => {
    if (!workerInstance) return
    workerInstance.addEventListener('message', processMessage)
    return () => workerInstance.removeEventListener('message', processMessage)
  })

  useEffect(() => {
    workerInstance?.search(searchCriteria)
  }, [searchCriteria, workerInstance])

  useEffect(() => {
    if (!location.search) return
    const s = new URLSearchParams(location.search).get('s')
    if (s && s !== searchCriteria) setSearchCriteria(s)
  }, [location.search]) // eslint-disable-line

  const goToRandomFrame = async () => {
    const frame = await workerInstance?.loadRandomFrame()
    navigate(framePath(frame))
  }

  return (<>
    <header>
      <a href="https://angelmakes.software/" className="header-angel" target="_blank" rel="noreferrer">💫 Angel</a>
      <h1>
        <Link to="/" className="site-title" onClick={() => setSearchCriteria('')}>
          🪐 {process.env.REACT_APP_TITLE} 🪐
        </Link>
      </h1>
      <label>
        <span>Search</span>
        <input
          autoFocus={true}
          type="text"
          placeholder={placeholders[placeholderIndex]}
          value={searchCriteria}
          ref={searchInputRef}
          onChange={(event) => {
            const value = event.target.value
            setSearchCriteria(value)
            navigate(
              { pathname: '/', search: value && new URLSearchParams({ s: value }).toString() },
              { replace: location.pathname === '/' }
            )
          }}
        />
        <button
          onClick={() => { navigate('/'); setSearchCriteria(''); searchInputRef.current?.focus() }}
          aria-label="Clear"
          id="clear"
          className={searchCriteria === '' ? 'hidden' : ''}
        ></button>
      </label>
      <button className="header-random" onClick={goToRandomFrame}>Random</button>
      <a href="https://github.com/Angel2mp3" className="header-github" target="_blank" rel="noreferrer" aria-label="GitHub">
        <svg viewBox="0 0 16 16" width="18" height="18" fill="currentColor"><path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/></svg>
      </a>
    </header>
    <main>
      <Outlet context={{ mosaicFrames, setMosaicFrames, workerInstance, ready, searchCriteria, searchResults, didSearch }} />
    </main>
    <footer>
      <div className="sister-sites">
        <span className="sister-label">Similar sites</span>
        <a href="https://reignofwinter.pages.dev/" className="sister-link" style={{ color: '#57dde9' }} target="_blank" rel="noreferrer">Pantheon</a>
      </div>
      <p className="dmca">
        <em>Scavengers Reign</em> &copy; Max Animation / Warner Bros. Discovery. Created by Joseph Bennett &amp; Charles Huettner. This is an unofficial fan-made visual archive with no affiliation to Max, Warner Bros. Discovery, or any associated rights holders. All images and dialogue excerpts are the property of their respective owners and are used here solely for non-commercial fan purposes. Use of brief clips and quotes may qualify as fair use under 17 U.S.C. &sect;&nbsp;107.
        DMCA takedown requests: <a href="mailto:angelmakessoftware@proton.me">angelmakessoftware@proton.me</a>
      </p>
      <p className="dmca">Built on top of the logic and ideas from <a href="https://github.com/seppo0010" target="_blank" rel="noreferrer">seppo0010</a> on GitHub.</p>
    </footer>
  </>)
}

const framePath = (frame: SearchResult) =>
  `/${encodeURIComponent(frame.season)}/${encodeURIComponent(frame.episode)}/${encodeURIComponent(frame.id)}`

const imageUrl = (frame: SearchResult) =>
  `${process.env.REACT_APP_ASSETS_URL}/${frame.season}x${('' + frame.episode).padStart(2, '0')}/${frame.id}_still.${process.env.REACT_APP_ASSETS_EXTENSION || 'png'}`

const thumbnailUrl = (frame: SearchResult) =>
  `${process.env.REACT_APP_ASSETS_URL}/${frame.season}x${('' + frame.episode).padStart(2, '0')}/${frame.id}_thumbnail.${process.env.REACT_APP_ASSETS_EXTENSION || 'png'}`

function MainRoute () {
  const { workerInstance, ready, searchCriteria, searchResults, didSearch } = useOutletContext<any>()
  return <Main
    searchResults={searchResults}
    didSearch={didSearch}
    searchCriteria={searchCriteria}
    ready={ready}
    workerInstance={workerInstance}
  />
}

function FrameRoute () {
  const { workerInstance, ready, searchCriteria, mosaicFrames, setMosaicFrames } = useOutletContext<any>()
  return <Frame
    workerInstance={workerInstance}
    ready={ready}
    searchCriteria={searchCriteria}
    mosaicFrames={mosaicFrames}
    setMosaicFrames={setMosaicFrames}
  />
}

// Space background: static stars + flying particles + shooting stars
function Stars () {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const isMobile = window.innerWidth <= 600

    let w = window.innerWidth
    let h = window.innerHeight
    canvas.width = w
    canvas.height = h

    type P = {
      x: number; y: number; r: number; opacity: number
      phase: number; phaseSpeed: number; amp: number
      color: string; vx: number; vy: number
    }
    type Shooter = { x: number; y: number; vx: number; vy: number; life: number; maxLife: number; width: number }

    const mkP = (rMin: number, rRange: number, opMin: number, opRange: number, ampVal: number, speedMult: number): P => {
      const angle = Math.random() * Math.PI * 2
      const speed = (Math.random() * 0.8 + 0.2) * speedMult
      const cols = [
        'rgba(220,235,255,', 'rgba(220,235,255,', 'rgba(255,255,255,',
        'rgba(200,225,255,', 'rgba(215,255,245,', 'rgba(255,225,180,',
      ]
      return {
        x: Math.random() * w,
        y: Math.random() * h,
        r: Math.random() * rRange + rMin,
        opacity: Math.random() * opRange + opMin,
        phase: Math.random() * Math.PI * 2,
        phaseSpeed: Math.random() * 0.015 + 0.003,
        amp: ampVal,
        color: cols[Math.floor(Math.random() * cols.length)],
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
      }
    }

    // Layer 1 — tiny static background stars (barely move)
    const bgStars: P[] = Array.from({ length: isMobile ? 130 : 600 }, () => mkP(0.15, 0.45, 0.12, 0.28, 0.08, 0.02))
    // Layer 2 — mid-ground drifters (gently float)
    const midDrift: P[] = Array.from({ length: isMobile ? 30 : 150 }, () => mkP(0.4, 0.8, 0.18, 0.32, 0.18, 0.45))
    // Layer 3 — foreground flyers (clearly moving, visible)
    const fgFlyers: P[] = Array.from({ length: isMobile ? 14 : 70 }, () => mkP(0.7, 1.2, 0.28, 0.38, 0.28, 1.15))

    let shooters: Shooter[] = []

    const wrap = (p: P) => {
      const pad = p.r + 4
      if (p.x < -pad) p.x = w + pad
      if (p.x > w + pad) p.x = -pad
      if (p.y < -pad) p.y = h + pad
      if (p.y > h + pad) p.y = -pad
    }

    const drawP = (p: P, glow: boolean) => {
      p.phase += p.phaseSpeed
      const a = Math.max(0.04, Math.min(1, p.opacity + p.amp * Math.sin(p.phase)))
      ctx.beginPath()
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
      ctx.fillStyle = `${p.color}${a})`
      if (glow && p.r > 0.75) { ctx.shadowBlur = p.r * 6; ctx.shadowColor = `${p.color}${a * 0.45})` }
      ctx.fill()
      ctx.shadowBlur = 0
      p.x += p.vx; p.y += p.vy
      wrap(p)
    }

    const onResize = () => { w = window.innerWidth; h = window.innerHeight; canvas.width = w; canvas.height = h }
    window.addEventListener('resize', onResize)

    let raf: number
    const draw = () => {
      ctx.clearRect(0, 0, w, h)
      for (const s of bgStars) drawP(s, false)
      for (const p of midDrift) drawP(p, true)
      for (const p of fgFlyers) drawP(p, true)

      // Shooting stars — fire every ~5s on average at 60fps
      if (Math.random() < (isMobile ? 0.006 : 0.028)) {
        const ang = (Math.random() * 45 + 8) * Math.PI / 180
        const spd = Math.random() * 11 + 7
        shooters.push({
          x: Math.random() * w * 0.8,
          y: Math.random() * h * 0.55,
          vx: Math.cos(ang) * spd,
          vy: Math.sin(ang) * spd,
          life: 0,
          maxLife: Math.random() * 28 + 38,
          width: Math.random() * 1.1 + 0.4,
        })
      }
      shooters = shooters.filter(s => s.life < s.maxLife)
      for (const ss of shooters) {
        const prog = ss.life / ss.maxLife
        const a = prog < 0.12 ? prog / 0.12 : Math.max(0, 1 - prog) * 1.1
        const tx = ss.x - ss.vx * 11; const ty = ss.y - ss.vy * 11
        const g = ctx.createLinearGradient(tx, ty, ss.x, ss.y)
        g.addColorStop(0, 'rgba(200,225,255,0)')
        g.addColorStop(1, `rgba(225,242,255,${a * 0.92})`)
        ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(ss.x, ss.y)
        ctx.strokeStyle = g; ctx.lineWidth = ss.width; ctx.stroke()
        ctx.beginPath(); ctx.arc(ss.x, ss.y, ss.width * 2, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(240,250,255,${a})`
        ctx.shadowBlur = 7; ctx.shadowColor = `rgba(180,220,255,${a * 0.55})`
        ctx.fill(); ctx.shadowBlur = 0
        ss.x += ss.vx; ss.y += ss.vy; ss.life++
      }
      raf = requestAnimationFrame(draw)
    }
    draw()
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize) }
  }, [])

  return <canvas ref={canvasRef} className="stars-canvas" />
}

function App () {
  const [loading, setLoading] = useState(false)
  const [ready, setReady] = useState(false)
  const [workerInstance, setWorkerInstance] = useState<any | null>(null)
  const [searchCriteria, setSearchCriteria] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [didSearch, setDidSearch] = useState(false)
  const [mosaicFrames, setMosaicFrames] = useState<string[]>([])

  useEffect(() => {
    ;(async () => {
      try {
        const newFont = new FontFace('planetvesta-font', `url(${process.env.REACT_APP_ASSETS_URL}/font.ttf)`)
        const timeout = new Promise<never>((resolve, reject) => setTimeout(() => reject(new Error('timeout')), 4000))
        const loadedFace = await Promise.race([newFont.load(), timeout])
        document.fonts.add(loadedFace)
      } catch (e) {
        // font unavailable or timed out, use fallback
      }
    })()
  }, []) // eslint-disable-line

  useEffect(() => {
    if (workerInstance) return
    setWorkerInstance(new Worker())
  }, [workerInstance])

  useEffect(() => {
    if (loading || !workerInstance) return
    setLoading(true)
    workerInstance.init()
  }, [loading, workerInstance])

  return (
    <>
    <Stars />
    <div id="main">
      <HashRouter>
        <Routes>
          <Route element={
            <WorkerTrigger
              workerInstance={workerInstance}
              searchCriteria={searchCriteria}
              setSearchCriteria={setSearchCriteria}
              setDidSearch={setDidSearch}
              setSearchResults={setSearchResults}
              setReady={setReady}
              ready={ready}
              searchResults={searchResults}
              didSearch={didSearch}
              mosaicFrames={mosaicFrames}
              setMosaicFrames={setMosaicFrames}
            />
          }>
            <Route path="/" element={<MainRoute />} />
            <Route path="/:season/:episode/:id" element={<FrameRoute />} />
          </Route>
        </Routes>
      </HashRouter>
    </div>
    </>
  )
}

export default App
